import { useEffect, useState } from 'react';
import {
  Page,
  Header,
  Content,
  InfoCard,
  Progress,
  WarningPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  useApi,
  fetchApiRef,
  discoveryApiRef,
  identityApiRef,
  configApiRef,
} from '@backstage/core-plugin-api';
import Button from '@material-ui/core/Button';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import GroupAddIcon from '@material-ui/icons/GroupAdd';

type TeamRow = {
  org: string;
  team_slug: string;
  team_id: number;
  team_name: string;
  description?: string | null;
  synced_at?: string;
};

type TeamMember = {
  user_login: string;
  user_id?: number;
  member_role?: string;
};

type TeamRepo = {
  repo: string;
  permission: 'read' | 'write' | 'admin';
};

type TeamDetails = {
  team: TeamRow | null;
  members: TeamMember[];
  repos: TeamRepo[];
};

export const TeamsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [teamDetails, setTeamDetails] = useState<TeamDetails | null>(null);
  const [teamDetailsLoading, setTeamDetailsLoading] = useState(false);
  const [teamDetailsError, setTeamDetailsError] = useState<
    string | undefined
  >();
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [addMemberUsername, setAddMemberUsername] = useState('');
  const [addMemberRole, setAddMemberRole] = useState<'member' | 'maintainer'>(
    'member',
  );
  const [addingMember, setAddingMember] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [createTeamDialogOpen, setCreateTeamDialogOpen] = useState(false);
  const [createTeamName, setCreateTeamName] = useState('');
  const [createTeamDescription, setCreateTeamDescription] = useState('');
  const [createTeamPrivacy, setCreateTeamPrivacy] = useState<
    'secret' | 'closed'
  >('secret');
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [confirmDeleteTeamOpen, setConfirmDeleteTeamOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<string | null>(null);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const configApi = useApi(configApiRef);

  const loadTeams = async (username?: string, userIsAdmin?: boolean) => {
    try {
      setError(undefined);
      setLoading(true);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');

      if (username && !userIsAdmin) {
        // Regular user: load only their teams
        const res = await fetchApi.fetch(
          `${adminBaseUrl}/github/users/${username}/teams`,
        );
        if (!res.ok) {
          throw new Error(`Failed to load teams: ${res.statusText}`);
        }
        const data = await res.json();
        setTeams(data.teams || []);
      } else {
        // Admin: load all teams
        const res = await fetchApi.fetch(`${adminBaseUrl}/github/teams`);
        if (!res.ok) {
          throw new Error(`Failed to load teams: ${res.statusText}`);
        }
        const data = await res.json();
        setTeams(data.teams || []);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const loadTeamDetails = async (teamSlug: string) => {
    try {
      setTeamDetailsError(undefined);
      setTeamDetailsLoading(true);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/teams/${teamSlug}`,
      );
      if (!res.ok) {
        throw new Error(`Failed to load team details: ${res.statusText}`);
      }
      const data = await res.json();
      setTeamDetails(data);
    } catch (e: unknown) {
      setTeamDetailsError(e instanceof Error ? e.message : String(e));
    } finally {
      setTeamDetailsLoading(false);
    }
  };

  const handleTeamClick = async (team: TeamRow) => {
    setSelectedTeam(team.team_slug);
    await loadTeamDetails(team.team_slug);
  };

  const handleCloseTeamDialog = () => {
    setSelectedTeam(null);
    setTeamDetails(null);
    setTeamDetailsError(undefined);
  };

  const handleAddMember = async () => {
    if (!addMemberUsername.trim() || !selectedTeam) {
      setError('Username is required');
      return;
    }
    try {
      setAddingMember(true);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/teams/${selectedTeam}/members/${addMemberUsername.trim()}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role: addMemberRole }),
        },
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to add member: ${res.statusText}`,
        );
      }
      setAddMemberDialogOpen(false);
      setAddMemberUsername('');
      setAddMemberRole('member');
      await loadTeamDetails(selectedTeam);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMemberClick = (username: string) => {
    setMemberToRemove(username);
    setConfirmRemoveOpen(true);
  };

  const handleConfirmRemove = async () => {
    if (!selectedTeam || !memberToRemove) return;
    try {
      setRemovingMember(memberToRemove);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/teams/${selectedTeam}/members/${memberToRemove}`,
        {
          method: 'DELETE',
        },
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to remove member: ${res.statusText}`,
        );
      }
      setConfirmRemoveOpen(false);
      setMemberToRemove(null);
      await loadTeamDetails(selectedTeam);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRemovingMember(null);
    }
  };

  const handleCreateTeam = async () => {
    if (!createTeamName.trim()) {
      setError('Team name is required');
      return;
    }
    try {
      setCreatingTeam(true);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(`${adminBaseUrl}/github/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createTeamName.trim(),
          description: createTeamDescription.trim() || undefined,
          privacy: createTeamPrivacy,
        }),
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to create team: ${res.statusText}`,
        );
      }
      setCreateTeamDialogOpen(false);
      setCreateTeamName('');
      setCreateTeamDescription('');
      setCreateTeamPrivacy('secret');
      // Reload teams
      await loadTeams(currentUser || undefined, isAdmin);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreatingTeam(false);
    }
  };

  const handleDeleteTeamClick = (teamSlug: string) => {
    setTeamToDelete(teamSlug);
    setConfirmDeleteTeamOpen(true);
  };

  const handleConfirmDeleteTeam = async () => {
    if (!teamToDelete) return;
    try {
      setDeletingTeam(true);
      setError(undefined);
      const backendUrl = await discoveryApi.getBaseUrl('backend');
      const adminBaseUrl = backendUrl.replace('/api/backend', '/api/admin');
      const res = await fetchApi.fetch(
        `${adminBaseUrl}/github/teams/${teamToDelete}`,
        {
          method: 'DELETE',
        },
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.detail || `Failed to delete team: ${res.statusText}`,
        );
      }
      setConfirmDeleteTeamOpen(false);
      setTeamToDelete(null);
      // Close team details dialog if it was open for this team
      if (selectedTeam === teamToDelete) {
        handleCloseTeamDialog();
      }
      // Reload teams
      await loadTeams(currentUser || undefined, isAdmin);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingTeam(false);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        // Get current user
        const identity = await identityApi.getBackstageIdentity();
        const userRef = identity.userEntityRef;
        const username = userRef.split('/').pop() || null;
        setCurrentUser(username);

        const adminGroups =
          configApi.getOptionalStringArray('permission.adminGroups') || [];

        const allEntityRefs = identity.ownershipEntityRefs || [];
        const userGroups = allEntityRefs
          .filter(ref => ref.startsWith('group:')) // Only group refs (format: group:default/admin)
          .map(ref => {
            const parts = ref.split('/');
            return parts[parts.length - 1]; // Extract group name/slug
          });

        const userIsAdmin =
          adminGroups.length > 0 &&
          adminGroups.some(adminGroup => userGroups.includes(adminGroup));

        setIsAdmin(userIsAdmin);

        // Load teams based on role
        await loadTeams(username || undefined, userIsAdmin);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const teamColumns: TableColumn<TeamRow>[] = [
    { title: 'Team', field: 'team_name' },
    { title: 'Slug', field: 'team_slug' },
    { title: 'Description', field: 'description' },
    ...(isAdmin
      ? [
          {
            title: 'Actions',
            render: (rowData: TeamRow) => (
              <Tooltip title="Delete team">
                <IconButton
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteTeamClick(rowData.team_slug);
                  }}
                  disabled={deletingTeam}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  const teamMemberColumns: TableColumn<TeamMember>[] = [
    { title: 'User', field: 'user_login' },
    { title: 'Role', field: 'member_role' },
    ...(isAdmin
      ? [
          {
            title: 'Actions',
            render: (rowData: TeamMember) => (
              <Tooltip title="Remove from team">
                <IconButton
                  size="small"
                  onClick={() => handleRemoveMemberClick(rowData.user_login)}
                  disabled={removingMember === rowData.user_login}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            ),
          },
        ]
      : []),
  ];

  const teamRepoColumns: TableColumn<TeamRepo>[] = [
    { title: 'Repository', field: 'repo' },
    { title: 'Permission', field: 'permission' },
  ];

  let body;
  if (loading) {
    body = <Progress />;
  } else if (error) {
    body = <WarningPanel title="Error" message={error} />;
  } else {
    body = (
      <InfoCard
        title={
          isAdmin
            ? 'Teams'
            : `My Teams${currentUser ? ` (${currentUser})` : ''}`
        }
        action={
          isAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<GroupAddIcon />}
              onClick={() => setCreateTeamDialogOpen(true)}
            >
              Create Team
            </Button>
          )
        }
      >
        <Table
          title="Teams"
          options={{ paging: true, pageSize: 10 }}
          columns={teamColumns}
          data={teams}
          onRowClick={(_, rowData) => handleTeamClick(rowData as TeamRow)}
        />
      </InfoCard>
    );
  }

  return (
    <Page themeId="tool">
      <Header
        title={isAdmin ? 'Teams Management' : 'My Teams'}
        subtitle={
          isAdmin ? 'Manage organization teams' : 'View your team memberships'
        }
      />
      <Content>{body}</Content>
      <Dialog
        open={selectedTeam !== null}
        onClose={handleCloseTeamDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {teamDetails?.team
            ? `${teamDetails.team.team_name} Details`
            : 'Team Details'}
        </DialogTitle>
        <DialogContent>
          {teamDetailsLoading && <Progress />}
          {!teamDetailsLoading && teamDetailsError && (
            <WarningPanel title="Error" message={teamDetailsError} />
          )}
          {!teamDetailsLoading && !teamDetailsError && teamDetails && (
            <>
              {teamDetails.team && (
                <div style={{ marginBottom: 16 }}>
                  <InfoCard
                    title="Team Information"
                    action={
                      isAdmin ? (
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PersonAddIcon />}
                          onClick={() => setAddMemberDialogOpen(true)}
                        >
                          Add Member
                        </Button>
                      ) : undefined
                    }
                  >
                    <p>
                      <strong>Name:</strong> {teamDetails.team.team_name}
                    </p>
                    <p>
                      <strong>Slug:</strong> {teamDetails.team.team_slug}
                    </p>
                    {teamDetails.team.description && (
                      <p>
                        <strong>Description:</strong>{' '}
                        {teamDetails.team.description}
                      </p>
                    )}
                    <p>
                      <strong>Members:</strong> {teamDetails.members.length}
                    </p>
                    <p>
                      <strong>Repositories:</strong> {teamDetails.repos.length}
                    </p>
                  </InfoCard>
                </div>
              )}
              <div style={{ marginBottom: 16 }}>
                <InfoCard title="Members">
                  <Table
                    title="Members"
                    options={{ paging: true, pageSize: 10 }}
                    columns={teamMemberColumns}
                    data={teamDetails.members}
                  />
                </InfoCard>
              </div>
              <div style={{ marginTop: 16 }}>
                <InfoCard title="Repositories">
                  <Table
                    title="Repositories"
                    options={{ paging: true, pageSize: 10 }}
                    columns={teamRepoColumns}
                    data={teamDetails.repos}
                  />
                </InfoCard>
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseTeamDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {isAdmin && (
        <Dialog
          open={addMemberDialogOpen}
          onClose={() => setAddMemberDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Add Member to Team</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="GitHub Username"
              value={addMemberUsername}
              onChange={e => setAddMemberUsername(e.target.value)}
              margin="normal"
              placeholder="Enter GitHub username"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={addMemberRole}
                onChange={e =>
                  setAddMemberRole(e.target.value as 'member' | 'maintainer')
                }
                label="Role"
              >
                <MenuItem value="member">Member</MenuItem>
                <MenuItem value="maintainer">Maintainer</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddMemberDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddMember}
              color="primary"
              variant="contained"
              disabled={addingMember || !addMemberUsername.trim()}
            >
              {addingMember ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog
          open={confirmRemoveOpen}
          onClose={() => setConfirmRemoveOpen(false)}
        >
          <DialogTitle>Confirm Removal</DialogTitle>
          <DialogContent>
            Are you sure you want to remove {memberToRemove} from this team?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmRemoveOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmRemove}
              color="secondary"
              variant="contained"
              disabled={removingMember !== null}
            >
              {removingMember ? 'Removing...' : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog
          open={createTeamDialogOpen}
          onClose={() => setCreateTeamDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Create New Team</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Team Name"
              value={createTeamName}
              onChange={e => setCreateTeamName(e.target.value)}
              margin="normal"
              placeholder="Enter team name"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={createTeamDescription}
              onChange={e => setCreateTeamDescription(e.target.value)}
              margin="normal"
              placeholder="Enter team description (optional)"
              multiline
              rows={3}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Privacy</InputLabel>
              <Select
                value={createTeamPrivacy}
                onChange={e =>
                  setCreateTeamPrivacy(e.target.value as 'secret' | 'closed')
                }
                label="Privacy"
              >
                <MenuItem value="secret">
                  Secret (only visible to team members)
                </MenuItem>
                <MenuItem value="closed">
                  Closed (visible to all org members)
                </MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreateTeamDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateTeam}
              color="primary"
              variant="contained"
              disabled={creatingTeam || !createTeamName.trim()}
            >
              {creatingTeam ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isAdmin && (
        <Dialog
          open={confirmDeleteTeamOpen}
          onClose={() => setConfirmDeleteTeamOpen(false)}
        >
          <DialogTitle>Confirm Team Deletion</DialogTitle>
          <DialogContent>
            Are you sure you want to delete team <strong>{teamToDelete}</strong>
            ? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDeleteTeamOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteTeam}
              color="secondary"
              variant="contained"
              disabled={deletingTeam}
            >
              {deletingTeam ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Page>
  );
};

export default TeamsPage;
